import React from "react";
import { Link } from "react-router-dom";
import { Download, Award, Shield, FileText, Sparkles, Briefcase, Users, ChevronRight } from "lucide-react";
import { useDocumentSeo, useJsonLd } from "../lib/seo";

export default function About() {
    useDocumentSeo({
        title: "About & Capability Statement — SBA WOSB/VOSB Certified | TT Admins",
        description: "Meet Christina Slimp and Admin This! N Thatz Syncd, LLC — an SBA-certified Woman-Owned & Veteran-Owned small business. Download the full Capability Statement. UEI MZYVNN8T4B71, CAGE 0SQV5.",
        canonical: "https://www.ttadmins.com/about",
    });
    useJsonLd([
        {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "About Admin This! N Thatz Syncd, LLC",
            url: "https://www.ttadmins.com/about",
            mainEntity: {
                "@type": "Organization",
                name: "Admin This! N Thatz Syncd, LLC",
                founder: { "@type": "Person", name: "Christina Slimp" },
                foundingDate: "2023",
                url: "https://www.ttadmins.com",
                logo: "https://www.ttadmins.com/logo-color.png",
                sameAs: ["https://sam.gov/entity/MZYVNN8T4B71"],
            },
        },
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://www.ttadmins.com/" },
                { "@type": "ListItem", position: 2, name: "About", item: "https://www.ttadmins.com/about" },
            ],
        },
    ]);
    return (
        <div data-testid="about-page">
            {/* Hero */}
            <section className="dawn-wash">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <div className="eyebrow mb-3">About & Capability</div>
                        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] font-light text-[var(--brand-navy)]">
                            Called to <span className="italic text-[var(--brand-coral)]">sync</span> excellence with empathy.
                        </h1>
                        <p className="mt-6 text-lg lg:text-xl text-slate-700 leading-relaxed max-w-2xl">
                            Founded and led by veteran and mother Christina Slimp, Admin This! N Thatz Syncd, LLC is a mission-driven, SBA-certified WOSB and VOSB delivering administrative excellence and community impact in equal measure.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <a href="/capability-statement.pdf" target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="about-pdf-btn">
                                <Download className="w-4 h-4 mr-2" /> Capability Statement PDF
                            </a>
                            <Link to="/contact" className="btn-outline-navy">Connect With Us</Link>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="aspect-[4/5] rounded-[2rem] overflow-hidden shadow-2xl">
                            <img src="https://images.pexels.com/photos/1181738/pexels-photo-1181738.jpeg" alt="Christina Slimp leadership" className="w-full h-full object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Company Overview */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
                <div className="grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4">
                        <div className="eyebrow mb-3">Company Overview</div>
                        <h2 className="font-serif text-4xl leading-tight text-[var(--brand-navy)]">Structural Sync for Mission-Led Organizations.</h2>
                    </div>
                    <div className="lg:col-span-8 space-y-5 text-slate-700 leading-relaxed text-lg">
                        <p>
                            Admin This! N Thatz Syncd, LLC is a mission-driven, SBA-certified Veteran-Owned Small Business (VOSB) and Woman-Owned Small Business (WOSB) providing administrative support and business management consulting.
                        </p>
                        <p>
                            We specialize in creating <em className="text-[var(--brand-coral)] not-italic font-semibold">"structural sync"</em> for organizations — streamlining operations, managing projects, and delivering data management solutions.
                        </p>
                        <p>
                            Through our <strong>E.M.P.O.W.E.R.I.N.G Veterans Workforce Initiative</strong>, we bridge the gap of veteran talent and workforce opportunity for ALL homeless veterans and their families — connecting them to housing pathways, online certification training, and the daily essentials that remove the survival struggle. With us, you're not just securing top-tier business solutions; you're partnering with a firm dedicated to uplifting and strengthening communities — federal, commercial, and faith-based alike.
                        </p>
                    </div>
                </div>
            </section>

            {/* Certifications */}
            <section className="sky-wash py-20 lg:py-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <div className="eyebrow mb-3 justify-center">Federal Certifications</div>
                        <h2 className="font-serif text-4xl sm:text-5xl text-[var(--brand-navy)]">Verified. Vetted. Ready.</h2>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {[
                            { icon: <Award />, label: "SBA Certified", value: "WOSB" },
                            { icon: <Shield />, label: "SBA Certified", value: "VOSB" },
                            { icon: <FileText />, label: "UEI", value: "MZYVNN8T4B71" },
                            { icon: <FileText />, label: "CAGE Code", value: "0SQV5" },
                        ].map((c, i) => (
                            <div key={i} className={`faith-card p-6 text-center fade-up delay-${i + 1}`}>
                                <div className="w-12 h-12 rounded-full bg-[var(--brand-gold)]/15 text-[var(--brand-gold-deep)] flex items-center justify-center mx-auto mb-4">
                                    {c.icon}
                                </div>
                                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">{c.label}</div>
                                <div className="font-serif text-lg text-[var(--brand-navy)]">{c.value}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Core competencies & differentiators */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
                <div className="grid lg:grid-cols-2 gap-10">
                    <div>
                        <div className="eyebrow mb-3">Core Competencies</div>
                        <h2 className="font-serif text-3xl text-[var(--brand-navy)] mb-6">What we do exceptionally.</h2>
                        <ul className="space-y-3" data-testid="competencies-list">
                            {[
                                "Business Strategy & Growth Consulting",
                                "Administrative & Executive Support",
                                "Business Operations & Project Management",
                                "Business Formation & Startup Structuring",
                                "Document Preparation & Data Management",
                                "Workforce Training & OJT Program Support",
                            ].map((item) => (
                                <li key={item} className="flex items-start text-slate-700">
                                    <ChevronRight className="w-5 h-5 mt-0.5 mr-2 text-[var(--brand-coral)] shrink-0" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <div className="eyebrow mb-3">Differentiators</div>
                        <h2 className="font-serif text-3xl text-[var(--brand-navy)] mb-6">Why partners choose us.</h2>
                        <ul className="space-y-3" data-testid="differentiators-list">
                            {[
                                "Veteran-Owned Leadership",
                                "SBA Certified VOSB & WOSB",
                                "Grant & Program Readiness Expertise",
                                "Administrative Infrastructure Specialists",
                                "Strategy + Implementation Approach",
                                "Mission-Driven Veteran Workforce Support",
                            ].map((item) => (
                                <li key={item} className="flex items-start text-slate-700">
                                    <Sparkles className="w-5 h-5 mt-0.5 mr-2 text-[var(--brand-gold-deep)] shrink-0" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Past Performance */}
            <section className="bg-white border-y border-slate-200 py-20 lg:py-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="eyebrow mb-3">Past Performance</div>
                    <h2 className="font-serif text-4xl text-[var(--brand-navy)] mb-12">A track record of synchronized excellence.</h2>
                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            {
                                name: "AdminEase Consulting",
                                tag: "Business Management Services",
                                items: ["Business formation services to streamline consulting operations", "\"Sync'd\" internal workflows, project management tracking & document preparation protocols"],
                                icon: <Briefcase />,
                            },
                            {
                                name: "Victoria's KeyGifts",
                                tag: "Business Establishment",
                                items: ["Business establishment, website creation & advertising strategy", "\"Online Presence Boosting\" package, community engagement & marketing consulting"],
                                icon: <Sparkles />,
                            },
                            {
                                name: "Hoosier Aire HVAC",
                                tag: "Logistics & Project Management",
                                items: ["Administrative logistics, data entry & compliance certificates", "Reorganized field-to-office communication & support staff flexibility"],
                                icon: <Users />,
                            },
                        ].map((c, i) => (
                            <div key={c.name} className={`faith-card p-7 fade-up delay-${i + 1}`}>
                                <div className="w-12 h-12 rounded-xl bg-[var(--brand-sky)] text-white flex items-center justify-center mb-5">{c.icon}</div>
                                <div className="font-serif text-xl text-[var(--brand-navy)]">{c.name}</div>
                                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mt-1 mb-4">{c.tag}</div>
                                <ul className="space-y-2 text-sm text-slate-600">
                                    {c.items.map((it) => (
                                        <li key={it} className="flex items-start"><ChevronRight className="w-4 h-4 mt-0.5 mr-1.5 text-[var(--brand-coral)] shrink-0" />{it}</li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* NAICS */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
                <div className="grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4">
                        <div className="eyebrow mb-3">Codes & Contracting</div>
                        <h2 className="font-serif text-3xl text-[var(--brand-navy)] mb-4">NAICS Codes</h2>
                        <p className="text-slate-600 leading-relaxed mb-6">For federal and commercial contracting reference.</p>
                        <a href="/capability-statement.pdf" target="_blank" rel="noopener noreferrer" className="btn-outline-navy" data-testid="naics-pdf-btn">
                            <Download className="w-4 h-4 mr-2" /> Full Capability PDF
                        </a>
                    </div>
                    <div className="lg:col-span-8">
                        <div className="grid grid-cols-3 sm:grid-cols-3 gap-3" data-testid="naics-grid">
                            {["561110", "541511", "561499", "541611", "541519", "541612", "611430"].map((code) => (
                                <div key={code} className="bg-white rounded-2xl border border-slate-200 p-5 text-center hover:border-[var(--brand-gold)] transition-colors">
                                    <div className="font-serif text-2xl text-[var(--brand-navy)]">{code}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* POC */}
            <section className="night-wash text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <div className="grid md:grid-cols-2 gap-10 items-center">
                        <div>
                            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-amber-200 mb-3">Point of Contact</div>
                            <h2 className="font-serif text-4xl leading-tight">Christina Slimp, CEO</h2>
                            <div className="mt-6 space-y-2 text-slate-300">
                                <div>1920 Lakepointe Dr, 114</div>
                                <div>Lewisville, TX 75057</div>
                                <a href="mailto:cslimp@ttadmins.com" className="block hover:text-amber-200">cslimp@ttadmins.com</a>
                                <a href="tel:+19729429022" className="block hover:text-amber-200">(972) 942-9022</a>
                                <a href="https://www.ttadmins.com/" className="block hover:text-amber-200">www.ttadmins.com</a>
                            </div>
                        </div>
                        <div>
                            <a href="/capability-statement.pdf" target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="poc-pdf-btn">
                                <Download className="w-4 h-4 mr-2" /> Download Capability Statement
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
